import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { JACE, TYPE_LABELS, fmtDateNL } from "./shared";

const CRITERIA = ["TECHNICAL EXECUTION", "ENGAGEMENT", "INTENSITY", "UNDERSTANDING", "DISCIPLINE", "SESSION FLOW"];
const OBJ_STATES = ["ACHIEVED", "PARTIALLY ACHIEVED", "NOT ACHIEVED"];
const BLOCK_STATES = ["COMPLETED", "MODIFIED", "SKIPPED", "REPEAT NEXT SESSION"];

// Session Review — na COMPLETE SESSION (Fase 3)
export default function JaceReview() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [actual, setActual] = useState("");
  const [assessment, setAssessment] = useState({});
  const [objReview, setObjReview] = useState({});
  const [blockReview, setBlockReview] = useState({});
  const [worked, setWorked] = useState("");
  const [improve, setImprove] = useState("");
  const [nextFocus, setNextFocus] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/jace/sessions/${sessionId}`).then(({ data }) => {
      setS(data);
      setActual(data.total_planned_time || data.planned_duration || "");
      const br = {};
      (data.blocks || []).forEach((b, i) => { br[i] = "COMPLETED"; });
      setBlockReview(br);
    }).catch(() => setS(false));
  }, [sessionId]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/jace/sessions/${sessionId}/complete`, {
        actual_duration: parseInt(actual) || null,
        assessment,
        objectives_review: objReview,
        blocks_review: blockReview,
        what_worked: worked,
        what_needs_improvement: improve,
        next_session_focus: nextFocus,
      });
      toast.success("Sessie afgerond & gereviewd");
      nav(`/jace/sessions/${sessionId}`);
    } catch { toast.error("Review opslaan mislukt"); }
    setBusy(false);
  };

  if (!s) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><span className="chip text-[#A3A3A3]">JACE // LOADING…</span></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F3F0] grain grain-dark px-6 py-8 md:px-10" data-testid="jace-review">
      <div className="max-w-2xl mx-auto">
        <div className="chip mb-2" style={{ color: JACE }}>// SESSION REVIEW — {s.session_id}</div>
        <h2 className="font-display uppercase text-4xl md:text-5xl mb-2">{s.title}</h2>
        <div className="chip text-[#A3A3A3] mb-8">{TYPE_LABELS[s.type]} · {fmtDateNL(s.date)} {s.start_time} · PLANNED {s.total_planned_time} MIN</div>

        <div className="mb-8">
          <label className="chip text-[#A3A3A3]">Actual duration (min)</label>
          <input type="number" min="0" value={actual} onChange={(e) => setActual(e.target.value)} className="editorial-input dark !w-32" data-testid="jace-review-actual" />
        </div>

        {/* Objectives */}
        {(s.objectives || []).length > 0 && (
          <div className="mb-8" data-testid="jace-review-objectives">
            <div className="chip text-[#A3A3A3] mb-3">— OBJECTIVE REVIEW</div>
            {s.objectives.map((o, i) => (
              <div key={i} className="border border-[#F5F3F0]/15 p-4 mb-2">
                <div className="text-sm text-[#DDD6CB] mb-3">{String(i + 1).padStart(2, "0")} — {o.title}</div>
                <div className="flex flex-wrap gap-2">
                  {OBJ_STATES.map((st) => (
                    <button key={st} onClick={() => setObjReview((p) => ({ ...p, [i]: st }))}
                      className="chip px-3 py-2 border transition-colors"
                      style={objReview[i] === st ? { borderColor: st === "NOT ACHIEVED" ? "#F87171" : JACE, color: st === "NOT ACHIEVED" ? "#F87171" : JACE } : { borderColor: "#F5F3F033", color: "#A3A3A3" }}
                      data-testid={`jace-obj-${i}-${st.replace(/ /g, "-")}`}>{st}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Blocks planned vs actual */}
        <div className="mb-8" data-testid="jace-review-blocks">
          <div className="chip text-[#A3A3A3] mb-3">— PLANNED VS ACTUAL (per block)</div>
          {(s.blocks || []).map((b, i) => (
            <div key={b.id || i} className="flex flex-wrap items-center gap-3 py-2.5 border-b border-[#F5F3F0]/10">
              <span className="text-sm text-[#DDD6CB] flex-1 min-w-32 truncate">{b.name}</span>
              <span className="chip text-[#4A4A4A]">{b.duration || 0} MIN</span>
              <select value={blockReview[i] || "COMPLETED"} onChange={(e) => setBlockReview((p) => ({ ...p, [i]: e.target.value }))} className="editorial-input dark !w-auto" data-testid={`jace-block-review-${i}`}>
                {BLOCK_STATES.map((st) => <option key={st} value={st} className="bg-[#0A0A0A]">{st}</option>)}
              </select>
            </div>
          ))}
        </div>

        {/* Coach assessment */}
        <div className="mb-8" data-testid="jace-review-assessment">
          <div className="chip text-[#A3A3A3] mb-3">— COACH ASSESSMENT (1–5)</div>
          {CRITERIA.map((c) => (
            <div key={c} className="flex items-center gap-3 py-2 border-b border-[#F5F3F0]/10">
              <span className="chip text-[#A3A3A3] flex-1">{c}</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setAssessment((p) => ({ ...p, [c]: n }))}
                  className="w-9 h-9 border font-mono text-sm transition-colors"
                  style={assessment[c] === n ? { background: JACE, color: "#0A0A0A", borderColor: JACE } : { borderColor: "#F5F3F033", color: "#A3A3A3" }}
                  data-testid={`jace-assess-${c.replace(/ /g, "-")}-${n}`}>{n}</button>
              ))}
            </div>
          ))}
        </div>

        <div className="space-y-5 mb-8">
          <div><label className="chip text-[#A3A3A3]">What worked?</label><textarea rows={2} value={worked} onChange={(e) => setWorked(e.target.value)} className="editorial-input dark w-full" data-testid="jace-review-worked" /></div>
          <div><label className="chip text-[#A3A3A3]">What needs improvement?</label><textarea rows={2} value={improve} onChange={(e) => setImprove(e.target.value)} className="editorial-input dark w-full" data-testid="jace-review-improve" /></div>
          <div><label className="chip text-[#A3A3A3]">Next session focus?</label><textarea rows={2} value={nextFocus} onChange={(e) => setNextFocus(e.target.value)} className="editorial-input dark w-full" data-testid="jace-review-next" /></div>
        </div>

        <div className="flex gap-3 pb-10">
          <button onClick={submit} disabled={busy} className="flex-1 py-4 chip" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-review-submit">{busy ? "Opslaan…" : "REVIEW OPSLAAN → SESSION REVIEWED"}</button>
          <button onClick={() => nav(`/jace/sessions/${sessionId}`)} className="btn-ghost-dark" data-testid="jace-review-cancel">Annuleer</button>
        </div>
      </div>
    </div>
  );
}
